import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tooltip } from 'react-tooltip';
import { HiOutlineShieldCheck, HiOutlineTrendingUp, HiOutlineSearch, HiOutlineEmojiSad, HiOutlineSortDescending, HiOutlineChevronDown, HiOutlineCash, HiOutlineChartBar, HiOutlineArrowSmUp, HiOutlineArrowSmDown, HiOutlineLightningBolt } from 'react-icons/hi';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip as RechartsTooltip, LabelList } from 'recharts';
import TokenStack from './TokenStack';
import ChainIcon from './ChainIcon';
import ProtocolIcon from './ProtocolIcon';
import { PROTOCOL_COLORS, CHAIN_COLORS, RISK_MAP } from '../data';
import { fmt } from '../utils';
import { useTheme } from '../theme';
import { useIsMobile } from '../useMediaQuery';

function PoolCard({ pool, onSelect, isCompare, onCompare, index }) {
  const { colors } = useTheme();
  const protocolColor = PROTOCOL_COLORS[pool.protocol] || colors.textPrimary;
  const chainColor = CHAIN_COLORS[pool.chain] || colors.textSecondary;
  const aprColor = pool.apr > 20 ? (colors.gold || '#8A6A00') : colors.green;
  const aprBgTint = pool.apr > 20 ? '#FFF8E0' : '#E8F8F2';
  const aprBorderAccent = pool.apr > 20 ? '#E0CC70' : '#A8DEC8';

  // Use shared tooltip IDs instead of per-card instances
  const sharedTooltipId = 'stables-shared-tooltip';

  const hasTrend = pool.apyPct1D !== undefined && pool.apyPct1D !== null;
  const trendUp = hasTrend && pool.apyPct1D >= 0;

  const metrics = [
    {
      label: "TVL",
      icon: <HiOutlineCash size={12} style={{ color: colors.labelLight }} />,
      value: fmt(pool.tvl),
    },
    {
      label: "VOL 24H",
      icon: <HiOutlineChartBar size={12} style={{ color: colors.labelLight }} />,
      value: fmt(pool.volume24h),
    },
    {
      label: "FEE",
      icon: <HiOutlineLightningBolt size={12} style={{ color: colors.labelLight }} />,
      value: pool.fee,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.03, 0.3) }}
      whileHover={{ scale: 1.02, y: -3, boxShadow: `0 8px 24px ${aprColor}20` }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect(pool)}
      data-tooltip-id={sharedTooltipId}
      data-tooltip-content={`${pool.name} \u2014 ${pool.apr.toFixed(2)}% APR \u2014 ${fmt(pool.tvl)} TVL \u2014 ${pool.protocol} on ${pool.chain}`}
      style={{
        background: colors.surface,
        borderRadius: 12,
        padding: 0,
        boxShadow: `0 1px 4px ${colors.shadow}`,
        border: isCompare ? `2px solid ${colors.green}` : `1px solid ${colors.border}`,
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Colored left border accent */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: 4,
        height: "100%",
        background: `linear-gradient(to bottom, ${aprColor}, ${aprBorderAccent})`,
        borderRadius: "12px 0 0 12px",
      }} />

      <div style={{ padding: "20px 20px 20px 24px" }}>
        {/* Compare checkbox with animated check */}
        <motion.div
          onClick={e => { e.stopPropagation(); onCompare(pool); }}
          whileHover={{ scale: 1.15 }}
          whileTap={{ scale: 0.9 }}
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            width: 22,
            height: 22,
            borderRadius: 6,
            border: `2px solid ${isCompare ? colors.green : colors.border}`,
            background: isCompare ? colors.green : "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "border-color 0.2s, background 0.2s",
          }}
        >
          <AnimatePresence mode="wait">
            {isCompare && (
              <motion.span
                key="check"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: "spring", stiffness: 500, damping: 25 }}
                style={{ color: "#FFF", fontSize: 12, fontWeight: 700, lineHeight: 1 }}
              >
                {"\u2713"}
              </motion.span>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <TokenStack tokens={pool.tokens} size={30} />
          <div>
            <div style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: 16,
              color: colors.textPrimary,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}>
              {pool.name}
              <span style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 2,
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 8,
                color: "#0D7A4E",
                background: "#E8F8F2",
                border: "1px solid #A8DEC8",
                padding: "2px 6px",
                borderRadius: 3,
                fontWeight: 600,
              }}>
                <HiOutlineShieldCheck size={9} />
                STABLE
              </span>
            </div>
            <div style={{ display: "flex", gap: 4, marginTop: 5 }}>
              <span
                data-tooltip-id={sharedTooltipId}
                data-tooltip-content={`Protocol: ${pool.protocol} \u2014 ${pool.audited ? 'Audited' : 'Not audited'}`}
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 9,
                  color: protocolColor,
                  background: `${protocolColor}12`,
                  border: `1px solid ${protocolColor}30`,
                  padding: "2px 7px",
                  borderRadius: 3,
                  fontWeight: 500,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                }}
              >
                <ProtocolIcon protocol={pool.protocol} size={10} />
                {pool.protocol}
              </span>
              {/* Shared tooltip rendered at view level */}
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9,
                color: chainColor,
                background: `${chainColor}12`,
                border: `1px solid ${chainColor}30`,
                padding: "2px 7px",
                borderRadius: 3,
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
              }}>
                <ChainIcon chain={pool.chain} size={10} />
                {pool.chain}
              </span>
            </div>
          </div>
        </div>

        {/* APR Section — prominent with background tint */}
        <div
          data-tooltip-id={sharedTooltipId}
          data-tooltip-html={`<div style="text-align:center"><strong>Base APR:</strong> ${(pool.apr * 0.7).toFixed(2)}%<br/><strong>Rewards:</strong> ${(pool.apr * 0.3).toFixed(2)}%${hasTrend ? `<br/><strong>24h Change:</strong> ${pool.apyPct1D >= 0 ? '+' : ''}${pool.apyPct1D.toFixed(2)}%` : ''}</div>`}
          style={{
            background: aprBgTint,
            borderRadius: 10,
            padding: "14px 16px",
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <HiOutlineTrendingUp size={22} style={{ color: aprColor, opacity: 0.7 }} />
            <div style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: 36,
              color: aprColor,
              lineHeight: 1,
            }}>
              {pool.apr.toFixed(2)}%
            </div>
          </div>
          {hasTrend && (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              fontWeight: 600,
              color: trendUp ? '#0D7A4E' : '#B8001A',
              background: trendUp ? '#E8F8F2' : '#FFF0F2',
              border: `1px solid ${trendUp ? '#A8DEC8' : '#F0A0A8'}`,
              padding: "3px 8px",
              borderRadius: 4,
            }}>
              {trendUp
                ? <HiOutlineArrowSmUp size={14} />
                : <HiOutlineArrowSmDown size={14} />
              }
              {Math.abs(pool.apyPct1D).toFixed(2)}%
            </div>
          )}
        </div>
        {/* Shared tooltip rendered at view level */}

        {/* Metrics */}
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {metrics.map((m, i) => (
            <div key={m.label} style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 0",
              borderBottom: i < metrics.length - 1 ? `1px solid ${colors.border}20` : "none",
            }}>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10,
                color: colors.labelLight,
                letterSpacing: 1,
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}>
                {m.icon}
                {m.label}
              </span>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 12,
                color: colors.textSecondary,
                fontWeight: 500,
              }}>
                {m.value}
              </span>
            </div>
          ))}
        </div>

        {/* Risk badge */}
        <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
          <span
            data-tooltip-id={sharedTooltipId}
            data-tooltip-content={
              pool.risk === 'LOW' ? 'Low risk: both tokens are recognized stablecoins'
              : pool.risk === 'MED' ? 'Medium risk: one volatile token + one stable'
              : 'High risk: both volatile or low liquidity'
            }
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 9,
              fontWeight: 600,
              color: RISK_MAP[pool.risk].color,
              background: RISK_MAP[pool.risk].bg,
              border: `1px solid ${RISK_MAP[pool.risk].border}`,
              padding: "3px 10px",
              borderRadius: 4,
              cursor: "default",
            }}
          >
            {pool.risk}
          </span>
          {/* Shared tooltip rendered at view level */}
        </div>
      </div>

      {/* Shared tooltip rendered at view level */}
    </motion.div>
  );
}

function CompareBanner({ pools, onClear }) {
  const { colors } = useTheme();

  if (pools.length < 2) return null;
  const [a, b] = pools;

  return (
    <div style={{
      background: colors.surface,
      borderRadius: 12,
      padding: "24px",
      boxShadow: `0 1px 4px ${colors.shadow}`,
      border: `2px solid ${colors.green}`,
      marginBottom: 20,
    }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 20,
      }}>
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10,
          color: colors.green,
          letterSpacing: 1.5,
          fontWeight: 600,
        }}>
          COMPARING POOLS
        </span>
        <button onClick={onClear} style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10,
          color: colors.labelMuted,
          background: "none",
          border: `1px solid ${colors.border}`,
          padding: "4px 10px",
          borderRadius: 4,
          cursor: "pointer",
        }}>
          Clear
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 1fr", gap: 0 }}>
        {/* Pool A */}
        <div style={{ textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
            <TokenStack tokens={a.tokens} size={24} />
          </div>
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 14, color: colors.textPrimary }}>
            {a.name}
          </div>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: colors.labelMuted, marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
            <ProtocolIcon protocol={a.protocol} size={10} /> {a.protocol} · <ChainIcon chain={a.chain} size={10} /> {a.chain}
          </div>
        </div>

        {/* VS */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11,
          color: colors.vsText,
          fontWeight: 600,
        }}>
          VS
        </div>

        {/* Pool B */}
        <div style={{ textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
            <TokenStack tokens={b.tokens} size={24} />
          </div>
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 14, color: colors.textPrimary }}>
            {b.name}
          </div>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: colors.labelMuted, marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
            <ProtocolIcon protocol={b.protocol} size={10} /> {b.protocol} · <ChainIcon chain={b.chain} size={10} /> {b.chain}
          </div>
        </div>
      </div>

      {/* Compare metrics */}
      <div style={{ marginTop: 20 }}>
        {[
          { label: "APR", aVal: `${a.apr.toFixed(2)}%`, bVal: `${b.apr.toFixed(2)}%`, aWins: a.apr > b.apr },
          { label: "TVL", aVal: fmt(a.tvl), bVal: fmt(b.tvl), aWins: a.tvl > b.tvl },
          { label: "VOL 24H", aVal: fmt(a.volume24h), bVal: fmt(b.volume24h), aWins: a.volume24h > b.volume24h },
        ].map(row => (
          <div key={row.label} style={{
            display: "grid",
            gridTemplateColumns: "1fr 100px 1fr",
            padding: "8px 0",
            borderBottom: `1px solid ${colors.borderSubtle}`,
            alignItems: "center",
          }}>
            <div style={{
              textAlign: "center",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 13,
              fontWeight: row.aWins ? 600 : 400,
              color: row.aWins ? colors.green : colors.loserText,
            }}>
              {row.aVal}
            </div>
            <div style={{
              textAlign: "center",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              color: colors.labelLight,
              letterSpacing: 1,
            }}>
              {row.label}
            </div>
            <div style={{
              textAlign: "center",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 13,
              fontWeight: !row.aWins ? 600 : 400,
              color: !row.aWins ? colors.green : colors.loserText,
            }}>
              {row.bVal}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TopPoolsChart({ pools, colors }) {
  const top5 = useMemo(() => {
    return [...pools]
      .sort((a, b) => b.apr - a.apr)
      .slice(0, 5)
      .map(p => ({
        name: p.name,
        apr: parseFloat(p.apr.toFixed(2)),
        protocol: p.protocol,
        chain: p.chain,
        tvl: p.tvl,
      }));
  }, [pools]);

  if (top5.length < 2) return null;

  const getBarColor = (protocol) => PROTOCOL_COLORS[protocol] || colors.green;

  const CustomTooltipContent = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <div style={{
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
          padding: "12px 16px",
          boxShadow: `0 4px 16px ${colors.shadow}`,
          minWidth: 160,
        }}>
          <div style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: 15,
            color: colors.textPrimary,
            marginBottom: 6,
          }}>
            {d.name}
          </div>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 13,
            color: colors.green,
            fontWeight: 600,
            marginBottom: 8,
          }}>
            {payload[0].value}% APR
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              color: colors.textSecondary,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}>
              <ProtocolIcon protocol={d.protocol} size={10} />
              {d.protocol}
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              color: colors.textSecondary,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}>
              <ChainIcon chain={d.chain} size={10} />
              {d.chain}
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              color: colors.labelMuted,
            }}>
              TVL: {fmt(d.tvl)}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const AprLabel = (props) => {
    const { x, y, width, value, index } = props;
    return (
      <text
        x={x + width + 6}
        y={y + 16}
        fontFamily="'IBM Plex Mono', monospace"
        fontSize={11}
        fontWeight={600}
        fill={getBarColor(top5[index]?.protocol)}
      >
        {value}%
      </text>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
      style={{
        background: colors.surface,
        borderRadius: 12,
        padding: "24px 28px",
        marginBottom: 20,
        boxShadow: `0 1px 4px ${colors.shadow}`,
        border: `1px solid ${colors.border}`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Subtle dot pattern background */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.03,
        backgroundImage: `radial-gradient(${colors.green} 1px, transparent 1px)`,
        backgroundSize: "20px 20px",
        pointerEvents: "none",
      }} />

      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 18,
        position: "relative",
      }}>
        <HiOutlineTrendingUp size={14} style={{ color: colors.green, opacity: 0.7 }} />
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10,
          color: colors.labelLight,
          letterSpacing: 1.5,
          fontWeight: 600,
        }}>
          TOP {top5.length} STABLECOIN POOLS BY APR
        </span>
      </div>
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        style={{ position: "relative" }}
      >
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={top5} layout="vertical" margin={{ top: 0, right: 50, left: 0, bottom: 0 }}>
            <XAxis
              type="number"
              tick={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, fill: colors.labelMuted }}
              axisLine={false}
              tickLine={false}
              tickFormatter={v => `${v}%`}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={90}
              tick={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, fill: colors.textSecondary }}
              axisLine={false}
              tickLine={false}
            />
            <RechartsTooltip content={<CustomTooltipContent />} cursor={{ fill: `${colors.border}30` }} />
            <Bar dataKey="apr" radius={[0, 6, 6, 0]} barSize={24} animationDuration={800} animationEasing="ease-out">
              {top5.map((entry, idx) => (
                <Cell key={idx} fill={getBarColor(entry.protocol)} fillOpacity={0.9} />
              ))}
              <LabelList content={<AprLabel />} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </motion.div>
    </motion.div>
  );
}

export default function StablecoinsView({ pools, onSelectPool }) {
  const { colors } = useTheme();
  const isMobile = useIsMobile();
  const [search, setSearch] = useState('');
  const [chain, setChain] = useState('All');
  const [protocol, setProtocol] = useState('All');
  const [sortBy, setSortBy] = useState('tvl');
  const [comparePools, setComparePools] = useState([]);
  const [searchFocused, setSearchFocused] = useState(false);
  const [visibleCount, setVisibleCount] = useState(30);

  const stablePools = useMemo(() => {
    let result = pools.filter(p => p.stablecoin);

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.tokens.some(t => t.toLowerCase().includes(q))
      );
    }

    if (chain !== 'All') result = result.filter(p => p.chain === chain);
    if (protocol !== 'All') result = result.filter(p => p.protocol === protocol);

    setVisibleCount(30); // Reset pagination on filter change
    return [...result].sort((a, b) => b[sortBy] - a[sortBy]);
  }, [pools, search, chain, protocol, sortBy]);

  const bestPool = useMemo(() => {
    const stables = pools.filter(p => p.stablecoin && p.tvl > 1000000);
    if (stables.length === 0) return null;
    return stables.reduce((best, p) => p.apr > best.apr ? p : best, stables[0]);
  }, [pools]);

  const handleCompare = (pool) => {
    setComparePools(prev => {
      const exists = prev.find(p => p.id === pool.id);
      if (exists) return prev.filter(p => p.id !== pool.id);
      if (prev.length >= 2) return [prev[1], pool];
      return [...prev, pool];
    });
  };

  // Derive from live stablecoin pool data
  const stablePoolsAll = useMemo(() => pools.filter(p => p.stablecoin), [pools]);
  const { totalTvl, avgApr } = useMemo(() => {
    const tvl = stablePoolsAll.reduce((sum, p) => sum + p.tvl, 0);
    const avg = stablePoolsAll.length > 0 ? stablePoolsAll.reduce((sum, p) => sum + p.apr, 0) / stablePoolsAll.length : 0;
    return { totalTvl: tvl, avgApr: avg };
  }, [stablePoolsAll]);
  const chains = useMemo(() => {
    const unique = [...new Set(stablePoolsAll.map(p => p.chain))].sort();
    return ["All", ...unique];
  }, [stablePoolsAll]);
  const protocols = useMemo(() => {
    const unique = [...new Set(stablePoolsAll.map(p => p.protocol))].sort();
    return ["All", ...unique];
  }, [stablePoolsAll]);
  const sorts = [
    { key: "apr", label: "APR" },
    { key: "tvl", label: "TVL" },
    { key: "volume24h", label: "VOL 24H" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      style={{
        padding: isMobile ? "12px 12px" : "20px 24px",
        maxWidth: 1200,
        margin: "0 auto",
      }}
    >
      {/* Banner */}
      {bestPool && (
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          style={{
            background: colors.bannerGradient,
            borderRadius: 12,
            padding: isMobile ? "20px 16px" : "28px 32px",
            marginBottom: 20,
            border: `1px solid ${colors.greenBorder}`,
            position: "relative",
            overflow: "hidden",
            boxShadow: `0 0 20px ${colors.green}10, 0 1px 4px ${colors.shadow}`,
          }}
        >
          {/* Animated gradient glow border */}
          <div style={{
            position: "absolute",
            top: -1,
            left: -1,
            right: -1,
            bottom: -1,
            borderRadius: 13,
            background: `linear-gradient(135deg, ${colors.green}20, transparent 40%, ${colors.green}10 60%, transparent 80%, ${colors.green}15)`,
            pointerEvents: "none",
            zIndex: 0,
          }} />

          {/* Subtle pattern overlay */}
          <div style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "40%",
            height: "100%",
            opacity: 0.04,
            backgroundImage: `radial-gradient(circle at 70% 50%, ${colors.green} 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />

          <div style={{ position: "relative", zIndex: 1 }}>
            {/* Label row */}
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 10,
              }}
            >
              <HiOutlineShieldCheck size={15} style={{ color: colors.green }} />
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10,
                color: colors.green,
                letterSpacing: 1.5,
                fontWeight: 600,
              }}>
                BEST STABLE APR TODAY
              </span>
            </motion.div>

            {/* APR number */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              style={{
                fontFamily: "'DM Serif Display', serif",
                fontSize: isMobile ? 36 : 48,
                color: colors.green,
                textShadow: `0 0 30px ${colors.green}25, 0 2px 4px ${colors.green}15`,
                lineHeight: 1.1,
              }}
            >
              {bestPool.apr.toFixed(2)}%
            </motion.div>

            {/* Pool info */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.35 }}
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 12,
                color: colors.textSecondary,
                marginTop: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              {bestPool.name} on <ProtocolIcon protocol={bestPool.protocol} size={12} /> {bestPool.protocol} · <ChainIcon chain={bestPool.chain} size={12} /> {bestPool.chain}
            </motion.div>

            {/* Stat cards row */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.5 }}
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                gap: isMobile ? 8 : 12,
                marginTop: 20,
                paddingTop: 18,
                borderTop: `1px solid ${colors.green}18`,
              }}
            >
              {[
                { label: "STABLE POOLS", display: String(stablePoolsAll.length) },
                { label: "TOTAL TVL", display: fmt(totalTvl) },
                { label: "AVG APR", display: `${avgApr.toFixed(2)}%` },
              ].map((stat) => (
                <div
                  key={stat.label}
                  style={{
                    background: `${colors.green}08`,
                    borderRadius: 8,
                    padding: "12px 14px",
                    border: `1px solid ${colors.green}12`,
                  }}
                >
                  <div style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 9,
                    color: colors.labelMuted,
                    letterSpacing: 1.2,
                    marginBottom: 4,
                  }}>
                    {stat.label}
                  </div>
                  <div style={{
                    fontFamily: "'DM Serif Display', serif",
                    fontSize: 20,
                    color: colors.green,
                  }}>
                    {stat.display}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      )}

      {/* Top Pools Chart */}
      <TopPoolsChart pools={stablePools} colors={colors} />

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.15 }}
        style={{
          background: colors.surface,
          borderRadius: 12,
          padding: isMobile ? "14px" : "16px 20px",
          marginBottom: 16,
          boxShadow: `0 1px 4px ${colors.shadow}`,
          border: `1px solid ${colors.border}`,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        {/* Search + Chain + Protocol row */}
        <div style={{
          display: "flex",
          alignItems: isMobile ? "stretch" : "center",
          gap: 10,
          flexWrap: "wrap",
          flexDirection: isMobile ? "column" : "row",
        }}>
          {/* Search input with icon */}
          <motion.div
            animate={{
              boxShadow: searchFocused
                ? `0 0 0 3px ${colors.green}20`
                : `0 0 0 0px transparent`,
              borderColor: searchFocused ? colors.green : colors.border,
            }}
            transition={{ duration: 0.2 }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 14px",
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              background: colors.inputBg,
              width: isMobile ? '100%' : 220,
              transition: "background-color 0.3s ease",
            }}
          >
            <HiOutlineSearch
              size={14}
              style={{
                color: searchFocused ? colors.green : colors.labelMuted,
                flexShrink: 0,
                transition: "color 0.2s ease",
              }}
            />
            <input
              type="text"
              placeholder="Search stable pools..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 12,
                border: "none",
                background: "transparent",
                color: colors.textPrimary,
                width: "100%",
                outline: "none",
              }}
            />
          </motion.div>

          {/* Chain pills */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {chains.map(c => (
              <motion.button
                key={c}
                onClick={() => setChain(c)}
                whileHover={{ scale: 1.04, y: -1 }}
                whileTap={{ scale: 0.96 }}
                animate={{
                  scale: chain === c ? 1.05 : 1,
                }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10,
                  padding: "5px 12px",
                  borderRadius: 20,
                  border: `1px solid ${chain === c ? colors.activeBg : colors.border}`,
                  background: chain === c ? colors.activeBg : colors.pillBg,
                  color: chain === c ? colors.activeText : colors.textSecondary,
                  cursor: "pointer",
                  fontWeight: chain === c ? 600 : 500,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {c !== "All" && <ChainIcon chain={c} size={12} />}
                {c}
              </motion.button>
            ))}
          </div>

          {/* Divider */}
          <div style={{
            width: 1,
            height: 20,
            background: colors.border,
            alignSelf: "center",
            display: isMobile ? "none" : "block",
          }} />

          {/* Protocol pills */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {protocols.map(p => (
              <motion.button
                key={p}
                onClick={() => setProtocol(p)}
                whileHover={{ scale: 1.04, y: -1 }}
                whileTap={{ scale: 0.96 }}
                animate={{
                  scale: protocol === p ? 1.05 : 1,
                }}
                transition={{ type: "spring", stiffness: 400, damping: 25 }}
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10,
                  padding: "5px 12px",
                  borderRadius: 20,
                  border: `1px solid ${protocol === p ? colors.activeBg : colors.border}`,
                  background: protocol === p ? colors.activeBg : colors.pillBg,
                  color: protocol === p ? colors.activeText : colors.textSecondary,
                  cursor: "pointer",
                  fontWeight: protocol === p ? 600 : 500,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {p !== "All" && <ProtocolIcon protocol={p} size={12} />}
                {p}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Sort row + pool count */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          borderTop: `1px solid ${colors.border}`,
          paddingTop: 12,
        }}>
          <HiOutlineSortDescending size={13} style={{ color: colors.labelLight, flexShrink: 0 }} />
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            color: colors.labelLight,
            letterSpacing: 1,
            flexShrink: 0,
          }}>
            SORT
          </span>
          {sorts.map(s => (
            <motion.button
              key={s.key}
              onClick={() => setSortBy(s.key)}
              whileHover={{ scale: 1.05, y: -1 }}
              whileTap={{ scale: 0.95 }}
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10,
                padding: "4px 10px",
                borderRadius: 4,
                border: `1px solid ${sortBy === s.key ? colors.activeBg : colors.border}`,
                background: sortBy === s.key ? colors.activeBg : "transparent",
                color: sortBy === s.key ? colors.activeText : colors.labelMuted,
                cursor: "pointer",
                fontWeight: 500,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              {s.label}
              {sortBy === s.key && (
                <HiOutlineChevronDown size={10} style={{ opacity: 0.7 }} />
              )}
            </motion.button>
          ))}

          <div style={{ flex: 1 }} />

          {/* Pool count badge */}
          <motion.div
            key={stablePools.length}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 10,
              color: colors.labelMuted,
              background: colors.pillBg || '#F4F5F6',
              border: `1px solid ${colors.border}`,
              padding: "4px 12px",
              borderRadius: 20,
            }}
          >
            <span style={{ fontWeight: 600, color: colors.textSecondary }}>
              {stablePools.length}
            </span>
            pools
            <span style={{ color: colors.border }}>|</span>
            Select 2 to compare
          </motion.div>
        </div>
      </motion.div>

      {/* Compare Banner */}
      <AnimatePresence>
        {comparePools.length >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <CompareBanner pools={comparePools} onClear={() => setComparePools([])} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pool Grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(280px, 1fr))",
        gap: isMobile ? 12 : 16,
      }}>
        {stablePools.slice(0, visibleCount).map((pool, index) => (
          <PoolCard
            key={pool.id}
            pool={pool}
            onSelect={onSelectPool}
            isCompare={comparePools.some(p => p.id === pool.id)}
            onCompare={handleCompare}
            index={index}
          />
        ))}
      </div>

      {/* Show more button */}
      {visibleCount < stablePools.length && (
        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <button
            onClick={() => setVisibleCount(prev => prev + 30)}
            style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 11,
              fontWeight: 600,
              color: colors.green,
              background: colors.greenBg || '#E8F8F2',
              border: `1px solid ${colors.greenBorder || '#A8DEC8'}`,
              borderRadius: 8,
              padding: '10px 28px',
              cursor: 'pointer',
            }}
          >
            Show more ({stablePools.length - visibleCount} remaining)
          </button>
        </div>
      )}

      {stablePools.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={{
            textAlign: "center",
            padding: "80px 20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
          }}
        >
          <motion.div
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <HiOutlineEmojiSad size={48} style={{ color: colors.border }} />
          </motion.div>
          <div style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: 18,
            color: colors.textSecondary,
          }}>
            No stable pools found
          </div>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            color: colors.labelMuted,
            maxWidth: 320,
            lineHeight: 1.6,
          }}>
            Try adjusting your search query, selecting a different chain, or changing the protocol filter.
          </div>
        </motion.div>
      )}

      {/* Single shared tooltip for all pool cards */}
      <Tooltip id="stables-shared-tooltip" style={{
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 11,
        borderRadius: 6,
        padding: '8px 12px',
        zIndex: 100,
      }} />
    </motion.div>
  );
}
